/**
 * ZANOS FAQ Header
 * Extends the theme FAQ index/search with category routing,
 * active states, mobile navigation and Help Center deep links.
 */

function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('faq-header')) {
  const FaqHeader = class extends HTMLElement {
    connectedCallback() {
      this.classNames = {
        questionContainerInactive: 'faq-search-item-inactive',
        sectionWithIndexStatus: 'section-faq-header--with-index',
        activeIndex: 'is-active'
      };

      this.searchInput = this.querySelector('.faq-search__input');

      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.performSearch.bind(this));
        this.searchInput.addEventListener('change', this.performSearch.bind(this));
        this.searchInput.addEventListener('paste', this.performSearch.bind(this));
      }

      this.debouncedBuildIndex = debounce(this.buildIndex.bind(this), 50);
      document.addEventListener(
        'theme:faq-header-update',
        this.debouncedBuildIndex
      );

      this.mobileButton = this.querySelector(
        '[data-zfaq-mobile-category]'
      );

      this.mobileMenu = this.querySelector(
        '[data-zfaq-mobile-menu]'
      );

      this.breadcrumbCurrent = this.querySelector(
        '[data-zfaq-current]'
      );

      if (this.mobileButton && this.mobileMenu) {
        this.mobileButton.addEventListener(
          'click',
          this.toggleMobileMenu.bind(this)
        );
      }

      this.labels = this.getCategoryLabels();
      this.requestedCategory = this.getRequestedCategory();

      this.debouncedBuildIndex();

      if (this.querySelector('.faq-index')) {
        this.addEventListener(
          'click',
          FaqHeader.handleIndexClick.bind(this)
        );

        const section = this.closest('.section-faq-header');

        if (section) {
          section.classList.add(
            this.classNames.sectionWithIndexStatus
          );
        }

        this.resizeIndex();

        this.debouncedResizeIndex = debounce(
          this.resizeIndex.bind(this),
          250
        );

        window.addEventListener(
          'resize',
          this.debouncedResizeIndex
        );
      }
    }

    disconnectedCallback() {
      document.removeEventListener(
        'theme:faq-header-update',
        this.debouncedBuildIndex
      );

      if (this.debouncedResizeIndex) {
        window.removeEventListener(
          'resize',
          this.debouncedResizeIndex
        );
      }

      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
      }
    }

    getCategoryLabels() {
      const data = this.querySelector('[data-zfaq-labels]');

      if (!data) return {};

      try {
        return JSON.parse(data.textContent);
      } catch (error) {
        return {};
      }
    }

    getRequestedCategory() {
      const params = new URLSearchParams(
        window.location.search
      );

      return params.get('category') || '';
    }

    normalize(value = '') {
      return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    categoryAliases() {
      return {
        orders: [
          'orders payments',
          'ordering',
          'order'
        ],

        shipping: [
          'shipping delivery',
          'shipping'
        ],

        returns: [
          'returns exchanges',
          'return exchange',
          'returns'
        ],

        size: [
          'size fit',
          'product sizing care',
          'sizing',
          'size'
        ],

        products: [
          'products care',
          'products quality',
          'product care',
          'products'
        ],

        account: [
          'account communication',
          'account'
        ],

        offers: [
          'offers gift cards',
          'gift cards promotions',
          'promotions',
          'offers'
        ],

        collaborations: [
          'collaborations',
          'collaboration'
        ],

        privacy: [
          'privacy data',
          'data privacy',
          'privacy'
        ],

        policies: [
          'policies',
          'policy'
        ]
      };
    }

    buildIndex() {
      const faqHeaderSection =
        this.closest('.section-faq-header');

      const indexContainer =
        this.querySelector('.faq-index__item-container');

      if (!faqHeaderSection) return;

      if (indexContainer) {
        indexContainer
          .querySelectorAll('.faq-index-item')
          .forEach((el) => el.remove());
      }

      this.linkedCollapsibleTabs = [];
      this.linkedQuestionContainers = [];
      this.linkedContent = [];
      this.indexEntries = [];

      let currentElement = faqHeaderSection;

      while (
        currentElement.nextElementSibling &&
        currentElement.nextElementSibling.classList.contains(
          'section-collapsible-tabs'
        )
      ) {
        currentElement =
          currentElement.nextElementSibling;

        this.linkedCollapsibleTabs.push(
          currentElement
        );

        currentElement
          .querySelectorAll('.collapsible-tabs__tab')
          .forEach((el) =>
            this.linkedQuestionContainers.push(el)
          );

        currentElement
          .querySelectorAll('.collapsible-tabs__content')
          .forEach((el) =>
            this.linkedContent.push(el)
          );

        const heading =
          currentElement.querySelector(
            '.collapsible-tabs__heading'
          );

        if (heading) {
          const entry = {
            section: currentElement,
            heading,
            id: heading.id,
            label: heading.textContent.trim()
          };
this.indexEntries.push(entry);

if (indexContainer) {
            const item =
              document.createElement('div');

            item.className =
              'faq-index-item';

            const link =
              document.createElement('a');

            link.className =
              'faq-index-item__link';

            link.href = `#${entry.id}`;
            link.textContent = entry.label;

            item.appendChild(link);
            indexContainer.appendChild(item);
          }
        }
      }

      if (indexContainer) {
        this.resizeIndex();
      }

      this.buildMobileMenu();
this.applyRequestedCategory();
this.applyRequestedHash();
this.bindActiveStateObserver();
    }

    buildMobileMenu() {
      if (
        !this.mobileMenu ||
        !this.indexEntries
      ) {
        return;
      }

      this.mobileMenu.innerHTML = '';

      this.indexEntries.forEach((entry) => {
        const link =
          document.createElement('a');

        link.href = `#${entry.id}`;
        link.textContent = entry.label;

        link.dataset.faqTarget =
          entry.id;

        link.addEventListener(
          'click',
          (event) => {
            event.preventDefault();

            this.scrollToTarget(
              entry.id,
              true
            );

            this.closeMobileMenu();
          }
        );

        this.mobileMenu.appendChild(link);
      });
    }
applyRequestedHash() {
  const id = decodeURIComponent(
    window.location.hash.replace('#', '')
  );

  if (!id || !this.indexEntries?.length) {
    return;
  }

  const entry = this.indexEntries.find(
    (item) => item.id === id
  );

  if (!entry) return;

  this.setActiveEntry(
    entry.id,
    entry.label
  );

  requestAnimationFrame(() => {
    setTimeout(() => {
      this.scrollToTarget(
        entry.id,
        false
      );
    }, 100);
  });
}
    findEntryForCategory(category) {
      if (
        !category ||
        !this.indexEntries?.length
      ) {
        return null;
      }

      const aliases =
        this.categoryAliases()[category] || [];

      const preferredLabel =
        this.labels?.[category] || '';

      const candidates = [
        preferredLabel,
        ...aliases
      ]
        .map((value) =>
          this.normalize(value)
        )
        .filter(Boolean);

      let best = null;
      let bestScore = 0;

      this.indexEntries.forEach((entry) => {
        const label =
          this.normalize(entry.label);

        let score = 0;

        candidates.forEach((candidate) => {
          if (label === candidate) {
            score = Math.max(score, 100);
          } else if (
            label.includes(candidate) ||
            candidate.includes(label)
          ) {
            score = Math.max(score, 60);
          } else {
            const words =
              candidate.split(' ');

            const matches =
              words.filter(
                (word) =>
                  word.length > 2 &&
                  label.includes(word)
              ).length;

            score = Math.max(
              score,
              matches * 15
            );
          }
        });

        if (score > bestScore) {
          best = entry;
          bestScore = score;
        }
      });

      return bestScore >= 15
        ? best
        : null;
    }

    applyRequestedCategory() {
      if (!this.requestedCategory) return;

      const entry =
        this.findEntryForCategory(
          this.requestedCategory
        );

      const requestedLabel =
        this.labels?.[
          this.requestedCategory
        ];

      if (requestedLabel) {
        if (this.breadcrumbCurrent) {
          this.breadcrumbCurrent.textContent =
            requestedLabel;
        }

        const mobileCurrent =
          this.querySelector(
            '[data-zfaq-mobile-current]'
          );

        if (mobileCurrent) {
          mobileCurrent.textContent =
            requestedLabel;
        }
      }

      if (!entry) return;

      this.setActiveEntry(
        entry.id,
        requestedLabel || entry.label
      );

      requestAnimationFrame(() => {
        setTimeout(() => {
          this.scrollToTarget(
            entry.id,
            false
          );
        }, 80);
      });
    }

    setActiveEntry(id, label) {
      this
        .querySelectorAll(
          '.faq-index-item__link'
        )
        .forEach((link) => {
          link.classList.toggle(
            this.classNames.activeIndex,
            link.getAttribute('href') ===
              `#${id}`
          );
        });

      if (this.mobileMenu) {
        this.mobileMenu
          .querySelectorAll('a')
          .forEach((link) => {
            link.classList.toggle(
              this.classNames.activeIndex,
              link.dataset.faqTarget === id
            );
          });
      }

      const mobileCurrent =
        this.querySelector(
          '[data-zfaq-mobile-current]'
        );

      if (
        mobileCurrent &&
        label
      ) {
        mobileCurrent.textContent =
          label;
      }

      if (
        this.breadcrumbCurrent &&
        label
      ) {
        this.breadcrumbCurrent.textContent =
          label;
      }
    }

    bindActiveStateObserver() {
      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
      }

      if (
        !this.indexEntries?.length ||
        !('IntersectionObserver' in window)
      ) {
        return;
      }

      this.sectionObserver =
        new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter(
                (entry) =>
                  entry.isIntersecting
              )
              .sort(
                (a, b) =>
                  Math.abs(
                    a.boundingClientRect.top
                  ) -
                  Math.abs(
                    b.boundingClientRect.top
                  )
              )[0];

            if (!visible) return;

            const heading =
              visible.target.querySelector(
                '.collapsible-tabs__heading'
              );

            if (!heading) return;

            this.setActiveEntry(
              heading.id,
              heading.textContent.trim()
            );
          },
          {
            rootMargin:
              '-18% 0px -68% 0px',
            threshold: 0
          }
        );

      this.indexEntries.forEach(
        (entry) =>
          this.sectionObserver.observe(
            entry.section
          )
      );
    }

    resizeIndex() {
      const stickyContainer =
        this.querySelector(
          '.faq-index__sticky-container'
        );

      const faqHeaderSection =
        this.closest(
          '.section-faq-header'
        );

      if (
        !stickyContainer ||
        !faqHeaderSection
      ) {
        return;
      }

      let currentElement =
        faqHeaderSection;

      while (
        currentElement.nextElementSibling &&
        currentElement.nextElementSibling.classList.contains(
          'section-collapsible-tabs'
        )
      ) {
        currentElement =
          currentElement.nextElementSibling;
      }

      const stickyContainerRect =
        stickyContainer.getBoundingClientRect();

      const currentElementRect =
        currentElement.getBoundingClientRect();

      stickyContainer.style.height =
        `${Math.max(
          0,
          currentElementRect.bottom -
            stickyContainerRect.top
        )}px`;
    }

    performSearch() {
      setTimeout(() => {
        if (!this.searchInput) return;

        const terms =
          this.searchInput.value
            .split(' ')
            .map((term) =>
              term
                .trim()
                .toLowerCase()
            )
            .filter(Boolean);

        this.linkedQuestionContainers.forEach(
          (el) => {
            if (!terms.length) {
              el.classList.remove(
                this.classNames
                  .questionContainerInactive
              );

              return;
            }

            const matchContent =
              el.textContent.toLowerCase();

            const found =
              terms.some((term) =>
                matchContent.includes(term)
              );

            el.classList.toggle(
              this.classNames
                .questionContainerInactive,
              !found
            );
          }
        );

        this.linkedContent.forEach(
          (el) => {
            el.classList.toggle(
              this.classNames
                .questionContainerInactive,
              terms.length > 0
            );
          }
        );
      }, 10);
    }

    toggleMobileMenu() {
      if (
        !this.mobileButton ||
        !this.mobileMenu
      ) {
        return;
      }

      const isOpen =
        this.mobileButton.getAttribute(
          'aria-expanded'
        ) === 'true';

      this.mobileButton.setAttribute(
        'aria-expanded',
        String(!isOpen)
      );

      this.mobileMenu.hidden =
        isOpen;

      this.mobileMenu.classList.toggle(
        'is-open',
        !isOpen
      );
    }

    closeMobileMenu() {
      if (
        !this.mobileButton ||
        !this.mobileMenu
      ) {
        return;
      }

      this.mobileButton.setAttribute(
        'aria-expanded',
        'false'
      );

      this.mobileMenu.hidden = true;

      this.mobileMenu.classList.remove(
        'is-open'
      );
    }

    scrollToTarget(
      id,
      updateHistory = false
    ) {
      const target =
        document.getElementById(id);

      if (!target) return;

      let y =
        target.getBoundingClientRect().top +
        window.pageYOffset -
        28;

      const stickyHeight =
        getComputedStyle(
          document.documentElement
        ).getPropertyValue(
          '--theme-sticky-header-height'
        );

      if (stickyHeight) {
        y -=
          parseInt(
            stickyHeight,
            10
          ) || 0;
      }

      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });

      const entry =
        this.indexEntries?.find(
          (item) => item.id === id
        );

      if (entry) {
        this.setActiveEntry(
          id,
          entry.label
        );
      }

      if (updateHistory) {
        const url =
          new URL(
            window.location.href
          );

        url.hash = id;

        history.replaceState(
          {},
          '',
          url
        );
      }
    }

    static handleIndexClick(evt) {
      const link =
        evt.target.closest(
          '.faq-index-item__link'
        );

      if (
        !link ||
        !this.contains(link)
      ) {
        return;
      }

      evt.preventDefault();

      const id =
        link
          .getAttribute('href')
          .replace('#', '');

      this.scrollToTarget(
        id,
        true
      );
    }
  };

  window.customElements.define(
    'faq-header',
    FaqHeader
  );
}